import React, { CSSProperties, useContext, useEffect, useRef, useState } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SignalWifiConnectedNoInternet4RoundedIcon from '@mui/icons-material/SignalWifiConnectedNoInternet4Rounded';
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded';
import WifiRoundedIcon from '@mui/icons-material/WifiRounded';
import { ConnectionManagerContext } from 'contexts/ConnectionManagerContext';
import GlobalContext from 'contexts/GlobalContext';
import { SAFE_AREA_TOP } from 'utils/safeArea';

export const CONNECTION_WARNING_DELAY_MS = 1200;
export const CONNECTION_UNSTABLE_THRESHOLD_MS = 10_000;
// How long the "Connection restored" confirmation stays up before auto-hiding.
export const CONNECTION_RESTORED_VISIBLE_MS = 2500;

export const BANDWIDTH_LOW_MESSAGE = 'Your internet connection is unstable. You may experience delays.';
export const NO_INTERNET_MESSAGE = 'You have lost your internet connection. Check your Wi-Fi or switch to a stronger network.';
export const RECONNECTING_MESSAGE = 'You have lost your internet connection. Attempting to reconnect...';
export const CONNECTION_RESTORED_MESSAGE = 'Connection restored.';

type Warning = {
  message: string;
  Icon: React.ElementType;
  tone: 'warning' | 'danger' | 'success';
};

const resolveWarning = (elapsedMs: number): Warning => {
  if (elapsedMs < CONNECTION_UNSTABLE_THRESHOLD_MS) {
    return {
      message: BANDWIDTH_LOW_MESSAGE,
      Icon: SignalWifiConnectedNoInternet4RoundedIcon,
      tone: 'warning',
    };
  }
  if (!navigator.onLine) {
    return {
      message: NO_INTERNET_MESSAGE,
      Icon: WifiOffRoundedIcon,
      tone: 'danger',
    };
  }
  return {
    message: RECONNECTING_MESSAGE,
    Icon: WifiOffRoundedIcon,
    tone: 'danger',
  };
};

// Per-tone colors: red = lost connection, amber = unstable, green = restored.
const TONE_PALETTE: Record<
  Warning['tone'],
  {
    border: string;
    glow: string;
    iconColor: string;
    iconBg: string;
    accent: string;
    pulseHalo: string;
  }
> = {
  danger: {
    border: 'rgba(239, 68, 68, 0.5)',
    glow: 'rgba(239, 68, 68, 0.16)',
    iconColor: '#fecaca',
    iconBg: 'rgba(239, 68, 68, 0.16)',
    accent: '#ef4444',
    pulseHalo: 'rgba(239, 68, 68, 0.14)'
  },
  warning: {
    border: 'rgba(233, 140, 38, 0.55)',
    glow: 'rgba(233, 140, 38, 0.16)',
    iconColor: '#fbbf24',
    iconBg: 'rgba(233, 140, 38, 0.18)',
    accent: '#E98C26',
    pulseHalo: 'rgba(233, 140, 38, 0.14)'
  },
  success: {
    border: 'rgba(34, 197, 94, 0.5)',
    glow: 'rgba(34, 197, 94, 0.16)',
    iconColor: '#bbf7d0',
    iconBg: 'rgba(34, 197, 94, 0.16)',
    accent: '#22c55e',
    pulseHalo: 'rgba(34, 197, 94, 0.14)'
  }
};

const ConnectionWarning = () => {
  const { connectionStatus } = useContext(ConnectionManagerContext);
  const { isLocationInvalid, geoCheckPending } = useContext(GlobalContext);
  const [visible, setVisible] = useState(false);
  const [warning, setWarning] = useState<Warning | null>(null);
  // Lets the player dismiss the banner manually (e.g. if the socket recovers but
  // the status never cleanly flips back). Reset when the connection is healthy
  // again so a fresh disconnect surfaces the warning once more.
  const [dismissed, setDismissed] = useState(false);
  const hasSeenConnectionActivityRef = useRef(false);
  const disconnectStartRef = useRef<number | null>(null);
  // True once a PROBLEM banner has actually been shown this outage, so on
  // recovery we flash "Connection restored" (instead of the banner just
  // vanishing) — and only when there was a visible problem to resolve.
  const problemShownRef = useRef(false);

  if (connectionStatus === 'connected' || connectionStatus === 'connecting') {
    hasSeenConnectionActivityRef.current = true;
  }

  const isProblematic =
    connectionStatus === 'reconnecting' ||
    (connectionStatus === 'disconnected' && hasSeenConnectionActivityRef.current);

  useEffect(() => {
    if (!isProblematic) {
      disconnectStartRef.current = null;
      setDismissed(false);

      // Recovered. If a problem banner was actually shown, flash a brief
      // "Connection restored" acknowledgement before hiding; otherwise (a blip
      // that never surfaced) just hide silently.
      if (problemShownRef.current) {
        problemShownRef.current = false;
        setWarning({
          message: CONNECTION_RESTORED_MESSAGE,
          Icon: WifiRoundedIcon,
          tone: 'success'
        });
        setVisible(true);
        const hideTimer = window.setTimeout(() => {
          setVisible(false);
          setWarning(null);
        }, CONNECTION_RESTORED_VISIBLE_MS);
        return () => window.clearTimeout(hideTimer);
      }

      setVisible(false);
      setWarning(null);
      return;
    }

    if (!disconnectStartRef.current) {
      disconnectStartRef.current = Date.now();
    }

    const tick = () => {
      const elapsed = Date.now() - (disconnectStartRef.current ?? Date.now());
      setWarning(resolveWarning(elapsed));
    };

    tick();
    const interval = window.setInterval(tick, 1000);
    const visibilityTimer = window.setTimeout(() => {
      setVisible(true);
      problemShownRef.current = true;
    }, CONNECTION_WARNING_DELAY_MS);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(visibilityTimer);
    };
  }, [isProblematic]);

  // THE LOCATION GATE OWNS THE SCREEN WHEN IT IS UP, AND IT OUTRANKS US.
  //
  // A geofence refusal at `$connect` arrives as a failed WebSocket handshake,
  // which is indistinguishable here from a dead network: the socket simply
  // never comes up. So this banner used to appear over a refused player and
  // tell them they had lost their internet connection, while they were sitting
  // on a working one. Worse, it is the branch BELOW `navigator.onLine` that
  // fired -- we had already checked, found the network up, and said it was down
  // anyway (EN-11114).
  //
  // The banner adds nothing once the region screen is up. That screen states
  // the real reason and tells the player what to do about it; this one offers a
  // wrong diagnosis and a Reconnect button that cannot succeed, because no
  // amount of reconnecting changes a location verdict.
  //
  // Same precedent as `notReady` in Lobby, which also stands down on
  // `isLocationInvalid`: when the location gate is blocking, it is the single
  // thing the player should be looking at.
  if (isLocationInvalid) return null;

  // NOR WHILE WE ARE STILL DECIDING. A login-time location check runs for as
  // long as a GPS fix and a vendor round trip take -- measured at ~9s on a real
  // device, and the cold-fix ceiling is far higher. The socket is refused for
  // that whole window, because `$connect` enforces on a verdict that does not
  // exist yet, so this banner sat there announcing a lost internet connection
  // on a working one until the verdict landed (EN-11114).
  //
  // We are not confused about the network here. We know exactly what is
  // happening and we are waiting for it. Saying nothing is honest; blaming the
  // player's Wi-Fi is not. The splash the location gate already shows is the
  // right thing in front of them.
  if (geoCheckPending) return null;

  if (!warning || !visible || dismissed) return null;

  const { Icon } = warning;
  const palette = TONE_PALETTE[warning.tone];

  return (
    <div
      role='alert'
      aria-live='assertive'
      style={{
        ...containerStyle,
        borderColor: palette.border,
        boxShadow: `0 14px 34px rgba(0, 0, 0, 0.55), 0 0 24px ${palette.glow}`
      }}>
      <div
        style={{
          ...iconWrapStyle,
          color: palette.iconColor,
          background: palette.iconBg
        }}>
        <Icon style={{ fontSize: '1.15rem' }} />
      </div>
      <div style={copyStyle}>
        <span style={messageStyle}>{warning.message}</span>
      </div>
      <span
        style={{
          ...pulseStyle,
          background: palette.accent,
          boxShadow: `0 0 0 6px ${palette.pulseHalo}`
        }}
        aria-hidden='true'
      />
      <button
        type='button'
        aria-label='Dismiss connection warning'
        onClick={() => setDismissed(true)}
        style={closeButtonStyle}>
        <CloseRoundedIcon style={{ fontSize: '1rem' }} />
      </button>
    </div>
  );
};

export default ConnectionWarning;

const containerStyle: CSSProperties = {
  position: 'fixed',
  // Offset by the device safe-area inset so the banner clears the notch /
  // status-bar clock on mobile (resolves to just 0.85rem on web/desktop).
  top: `calc(0.85rem + ${SAFE_AREA_TOP})`,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 5000,
  display: 'flex',
  alignItems: 'center',
  gap: '0.65rem',
  width: 'min(420px, calc(100vw - 1.5rem))',
  padding: '0.7rem 0.85rem',
  background: 'rgba(14, 23, 36, 0.96)',
  backdropFilter: 'blur(12px)',
  border: '1px solid',
  borderRadius: '12px',
  color: '#fff',
  fontFamily: "'Outfit', sans-serif",
  pointerEvents: 'none'
};

const iconWrapStyle: CSSProperties = {
  width: '34px',
  height: '34px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
};

const copyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.08rem',
  minWidth: 0,
  flex: 1
};

const messageStyle: CSSProperties = {
  fontSize: '0.8rem',
  lineHeight: 1.35,
  color: 'rgba(255, 255, 255, 0.92)'
};

const pulseStyle: CSSProperties = {
  width: '8px',
  height: '8px',
  borderRadius: '999px',
  flexShrink: 0
};

const closeButtonStyle: CSSProperties = {
  pointerEvents: 'auto',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '26px',
  height: '26px',
  padding: 0,
  border: 'none',
  borderRadius: '8px',
  background: 'rgba(255, 255, 255, 0.08)',
  color: 'rgba(255, 255, 255, 0.7)',
  cursor: 'pointer'
};
